"use client";

import { useState, useEffect } from "react";
import { useParams, useApiReady } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  fetchSubscriptionSummary,
} from "@/store/modules/SubscriptionsAdmin/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { subscriptionSchema } from "@/validations";

const useSubscriptions = (onSuccess?: () => void, customPerPage?: number) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(subscriptionSchema),
    defaultValues: {
      client_id: "",
      subscription_id: "",
    },
  });

  const setFormData = (
    data: Partial<typeof subscriptionSchema.__outputType>,
  ) => {
    reset(data);
  };

  useEffect(() => {
    const subscription = watch((values) => {
      console.log("Valores do formulário:", values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: typeof subscriptionSchema.__outputType) => {
    try {
      let result;

      result = await dispatch(
        createSubscription({ subscription_id: subscriptionSlug, data }),
      );

      if (createSubscription.fulfilled.match(result)) {
        if (onSuccess) onSuccess();
        const customParams = customPerPage
          ? { ...params, per_page: customPerPage }
          : params;
        dispatch(fetchSubscriptions({ params: customParams }));
        reset();
        setSubscriptionId(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const subscription = useSelector(
    (state: any) => state.subscription.subscriptions.data,
  );
  const loading = useSelector((state: any) => state.subscription.loading);
  const error = useSelector((state: any) => state.subscription.error);
  const subscriptionSummary = useSelector(
    (state: any) => state.subscription.subscriptionSummary,
  );

  const dispatch: AppDispatch = useDispatch();
  const [subscriptionSlug, setSubscriptionId] = useState<any>();
  const { isReady: isApiReady } = useApiReady(2000);

  useEffect(() => {
    if (!isApiReady) return;
    const customParams = customPerPage
      ? { ...params, per_page: customPerPage }
      : params;
    dispatch(fetchSubscriptions({ params: customParams }));
    dispatch(fetchSubscriptionSummary());
  }, [isApiReady, params, customPerPage, dispatch]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const totalItems = subscription?.total ?? 0;
  const itemsPerPage = subscription?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(
    subscription?.current_page ?? 1,
  );

  useEffect(() => {
    if (
      subscription?.current_page &&
      subscription.current_page !== currentPage
    ) {
      setCurrentPage(subscription.current_page);
    }
  }, [subscription?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  const handleDelete = async () => {
    try {
      if (subscriptionSlug) {
        await dispatch(deleteSubscription(subscriptionSlug));
        setSubscriptionId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao excluir subscription:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    subscription,
    loading,
    error,
    setSubscriptionId,
    handleDelete,
    setFormData,
    getValues,
    control,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    setParams,
    handleFilterChange,
    searchValue,
    subscriptionSummary,
  };
};

export default useSubscriptions;
